import {
    ColumnIndex,
    ColumnKey,
    IGridCell,
    IMutableGridModel,
    RowIndex
} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {FrameEntry} from '@mintdata-wsp/wsp/controllers/visualDesigner/visualData/VisualDataManager';
import {FrameCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/frame/FrameCell';
import {FrameColumnHeaderCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/frame/FrameColumnHeaderCell';
import {FrameRowHeaderCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/frame/FrameRowHeaderCell';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';

export class VisualDataUtil
{
    public static addRowToGridModel(entry:FrameEntry, gridModel:IMutableGridModel):void
    {
        const {frame} = entry;

        if( frame.isEmpty )
            return;

        const newColumnCells:FrameColumnHeaderCell[] = this._computeNewColumnsToAdd(entry, gridModel);
        const newRows = this._computeRowsToAppend(entry, newColumnCells, gridModel);

        const columnFillerForPrevRows = (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
        {
            return FrameCell.newEmptyCell();
        };

        const origNumColumns = gridModel.getBody().getNumColumns();
        const newColumnKeys = newColumnCells.map((c:FrameColumnHeaderCell) => (c.columnName));

        const createColumnHeader = (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
        {
            let offset = columnIndex - origNumColumns;
            return newColumnCells[offset];
        };

        const createRowHeader = (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
        {
            return FrameRowHeaderCell.forRow(rowIndex);
        };

        gridModel.appendDataRowsAndColumns(newRows,
            createRowHeader,
            newColumnKeys,
            createColumnHeader,
            columnFillerForPrevRows);

        // gridModel.print();
    }

    private static _computeNewColumnsToAdd(entry:FrameEntry, gridModel:IMutableGridModel):FrameColumnHeaderCell[]
    {
        const fields = entry.frame.getFields();
        const columnsToAdd:FrameColumnHeaderCell[] = [];

        // this._addColumnsForFrameMeta(entry, gridModel, columnsToAdd);

        for( let field of fields )
            this._createNewColumnsIfNeeded(field, gridModel, columnsToAdd);

        return columnsToAdd;
    }

    private static _addColumnsForFrameMeta(entry:FrameEntry, gridModel:IMutableGridModel, columnsToAdd:FrameColumnHeaderCell[])
    {
        const frameMeta = entry.frame.getMeta();
        if( frameMeta )
        {
            let metaKeys = frameMeta.getKeys();

            for( let metaKey of metaKeys )
            {
                if( gridModel.getIndexOfColumn(metaKey, true) === -1 )
                    columnsToAdd.push(FrameColumnHeaderCell.forFrameMeta(metaKey));
            }
        }
    }

    private static _createNewColumnsIfNeeded(field:FrameField,
                                             gridModel:IMutableGridModel,
                                             columnsToAdd:FrameColumnHeaderCell[]):void
    {
        const columnKey = field.name;
        const columnOffset = gridModel.getIndexOfColumn(columnKey, true);

        if( columnOffset === -1 )
            columnsToAdd.push(FrameColumnHeaderCell.fromField(field));
        else
        {
            const columnHeaders = gridModel.getColumnHeaders();
            const headerCell = gridModel.isTransposed() ?
                columnHeaders.getCellAt(columnOffset, 0) as FrameColumnHeaderCell
                :
                columnHeaders.getCellAt(0, columnOffset) as FrameColumnHeaderCell;

            const isSameType = headerCell &&
                headerCell.type &&
                headerCell.type.isEqualTo(field.type);

            if( !isSameType )
            {
                const newColumnKey = this._findAvailableColumnKey(gridModel, columnKey, 2);
                columnsToAdd.push(FrameColumnHeaderCell.fromFieldWithKey(newColumnKey, field));
            }
        }
    }

    private static _findAvailableColumnKey(gridModel:IMutableGridModel, currentPrefix:string, currentSuffix:number):ColumnKey
    {
        const columnKey = `${currentPrefix}-${currentSuffix}`;

        if( gridModel.getIndexOfColumn(columnKey, false) === -1 )
            return columnKey;
        else
            return this._findAvailableColumnKey(gridModel, currentPrefix, currentSuffix + 1);
    }

    private static _computeRowsToAppend(entry:FrameEntry,
                                        newColumnCells:FrameColumnHeaderCell[],
                                        gridModel:IMutableGridModel):IGridCell[][]
    {
        const timestamp = entry.timestamp;
        const frame = entry.frame;
        const records = frame.getRecords();
        const fields = frame.getFields();

        const origNumColumns = !gridModel.isTransposed() ?
            gridModel.getBody().getNumColumns()
            :
            gridModel.getBody().getNumRows();

        const numColumns = origNumColumns + newColumnCells.length;
        const newRows:IGridCell[][] = new Array(records.length);

        let newRowIndex = 0;
        for( let record of records )
        {
            const newRow = newRows[newRowIndex] = [];

            for( let i = 0; i < numColumns; i++ )
                newRow[i] = null;

            for( let field of fields )
            {
                let columnIndex = gridModel.getIndexOfColumn(field.name, false);

                if( columnIndex === -1 )
                    columnIndex = origNumColumns + newColumnCells.findIndex((c:FrameColumnHeaderCell) => (c.columnName === field.name));

                if( columnIndex === -1 )
                    console.error(`Error: cannot determine columnIndex for field name: ${field.name}`);
                else
                {
                    const value:IValue = record.getCell(field.name, true);
                    const gridCell = FrameCell.fromValue(value);

                    newRow[columnIndex] = gridCell;
                }
            }

            for( let i = 0; i < numColumns; i++ )
            {
                if( newRow[i] === null )
                    newRow[i] = FrameCell.newEmptyCell();
            }

            newRowIndex++;
        }

        return newRows;
    }
}
